/**
 * Spec 051 — error-screen parity, Phase 1 (observe).
 *
 * The single shared treatment (D3) that brings the four previously-silent
 * caregiver-facing error screens under one canonical appearance record. Each
 * screen calls {@link recordScreenShown} once, from its own `ngAfterViewInit`,
 * AFTER it renders (D1: the screen is the emitter, not whatever routed to it).
 *
 * Design constraints honoured here:
 *  - The fatal surface must never recurse: every path is guarded and telemetry
 *    failures are swallowed.
 *  - It REUSES spec 049's diagnostic payload and context sources (build identity
 *    meta tag, `navigator.onLine`, incident code) rather than inventing a
 *    parallel one; the stale-build async probes (staleness / service worker) are
 *    recovery machinery and are deliberately left `unresolved` in this
 *    observe-only phase (FR-003).
 */
import { DOCUMENT } from '@angular/common';
import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { AppInsightsService } from './app-insights.service';
import {
  UnicornConnectivityState,
  UnicornRecoveryOutcome,
  createUnicornIncidentCode,
  normalizeUnicornBuildIdentity,
  normalizeUnicornRoute,
} from '../domain/unicorn';
import {
  ERROR_SCREEN_EVENT_NAME,
  ERROR_SCREEN_RECOVERY_OUTCOME_EVENT_NAME,
  ERROR_SCREEN_RECOVERY_PENDING_KEY,
  ERROR_SCREEN_RECOVERY_PENDING_TTL_MS,
  ErrorScreen,
  ErrorScreenPendingRecovery,
  createErrorScreenRecoveryOutcomeProperties,
  createErrorScreenShownProperties,
  parseErrorScreenPendingRecovery,
} from '../domain/error-screen';

export interface ErrorScreenRecordOptions {
  /** Spec-049 cause taxonomy value describing what went wrong. */
  cause?: unknown;
  /** Spec-049 producer dimension; `unknown` where no producer handed a cause. */
  producer?: unknown;
  /**
   * A machine-readable reason handed over on arrival (FR-004). Vetted before it
   * is recorded — see {@link normalizeArrivalReason}. Never a free-text value.
   */
  arrivalReason?: unknown;
}

@Injectable({ providedIn: 'root' })
export class ErrorScreenTelemetryService {
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);
  /**
   * Incident codes whose recovery outcome has already been emitted this app
   * lifetime, so a re-render or a burst of NavigationEnd events cannot record
   * the same outcome twice.
   */
  private readonly resolvedRecoveries = new Set<string>();

  /**
   * Emits exactly one canonical `error_screen_shown` record for the given
   * screen (FR-001). Safe to call from any error screen; never throws.
   */
  recordScreenShown(
    screen: ErrorScreen,
    options: ErrorScreenRecordOptions = {},
  ): void {
    try {
      const properties = createErrorScreenShownProperties(
        screen,
        options.cause,
        options.producer,
        this.getBuildIdentity(),
        this.getConnectivity(),
        this.getIncidentCode(),
        options.arrivalReason,
      );
      const appInsights = this.injector.get(AppInsightsService);
      appInsights.trackEvent(ERROR_SCREEN_EVENT_NAME, properties);
      // A fatal-error surface may be torn down immediately; flush so the record
      // is not lost to the batch window.
      appInsights.flush();
    } catch {
      // The error surface must not recurse if telemetry is unavailable.
    }
  }

  /**
   * Persists the pending recovery record the instant a caregiver takes a
   * recovery action, BEFORE the (often full-page) navigation that action
   * triggers. Returns whether the outcome will be measurable: `false` when the
   * record could not be written (no/again-readable storage), so the caller can
   * emit an `unmeasurable` outcome synchronously rather than lose the signal.
   *
   * FR-012: whether a recovery succeeded must be observable. FR-011: only vetted
   * values are stored (the record is assembled from a closed set of primitives).
   */
  writePendingRecovery(pending: ErrorScreenPendingRecovery): boolean {
    const storage = this.getStorage();
    if (!storage) {
      return false;
    }
    // A still-unresolved earlier pending means the previous attempt never
    // reached a landing (e.g. the caregiver clicked again). Resolve it as a
    // failure so it is not silently overwritten and lost.
    try {
      const previous = parseErrorScreenPendingRecovery(
        storage.getItem(ERROR_SCREEN_RECOVERY_PENDING_KEY),
      );
      if (previous && !this.resolvedRecoveries.has(previous.incidentCode)) {
        const now = this.getNow();
        const outcome: UnicornRecoveryOutcome =
          now - previous.startedAt >= ERROR_SCREEN_RECOVERY_PENDING_TTL_MS
            ? 'expired'
            : 'failure';
        this.emitOutcome(previous, outcome, now);
      }
    } catch {
      // A best-effort cleanup of a stale record must never block a new attempt.
    }

    try {
      const serialized = JSON.stringify(pending);
      storage.setItem(ERROR_SCREEN_RECOVERY_PENDING_KEY, serialized);
      return storage.getItem(ERROR_SCREEN_RECOVERY_PENDING_KEY) === serialized;
    } catch {
      return false;
    }
  }

  /**
   * Resolves a pending recovery against the URL just landed on and emits exactly
   * one canonical `error_screen_recovery_outcome` (FR-012). Called from the
   * app's `NavigationEnd` hook. A full-page recovery (re-auth, retry-reload)
   * survives the reload in sessionStorage and is resolved on the return
   * navigation, exactly as spec 049's re-auth outcome is.
   *
   * Classification (reusing 049's vocabulary): a working route is `success`;
   * landing back on any error screen is `failure`; an elapsed TTL is `expired`;
   * an unremovable record is `unmeasurable`.
   */
  resolveRecoveryOutcome(url: string): void {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }
    let pending: ErrorScreenPendingRecovery | undefined;
    try {
      pending = parseErrorScreenPendingRecovery(
        storage.getItem(ERROR_SCREEN_RECOVERY_PENDING_KEY),
      );
    } catch {
      return;
    }
    if (!pending || this.resolvedRecoveries.has(pending.incidentCode)) {
      return;
    }

    const now = this.getNow();
    const destination = normalizeUnicornRoute(url);
    // Re-auth passes THROUGH /callback before the real landing; do not resolve
    // on that transient hop while the record is still within its horizon.
    if (
      destination === '/callback' &&
      now - pending.startedAt < ERROR_SCREEN_RECOVERY_PENDING_TTL_MS
    ) {
      return;
    }

    const outcome: UnicornRecoveryOutcome =
      now - pending.startedAt >= ERROR_SCREEN_RECOVERY_PENDING_TTL_MS
        ? 'expired'
        : this.isFailureDestination(destination)
          ? 'failure'
          : 'success';
    const resolved: ErrorScreenPendingRecovery = { ...pending, destination };

    let persistedOutcome: UnicornRecoveryOutcome = outcome;
    try {
      storage.removeItem(ERROR_SCREEN_RECOVERY_PENDING_KEY);
      if (storage.getItem(ERROR_SCREEN_RECOVERY_PENDING_KEY) !== null) {
        persistedOutcome = 'unmeasurable';
      }
    } catch {
      persistedOutcome = 'unmeasurable';
    }

    this.emitOutcome(resolved, persistedOutcome, now);
  }

  private emitOutcome(
    pending: ErrorScreenPendingRecovery,
    outcome: UnicornRecoveryOutcome,
    now: number,
  ): void {
    this.resolvedRecoveries.add(pending.incidentCode);
    try {
      const appInsights = this.injector.get(AppInsightsService);
      appInsights.trackEvent(
        ERROR_SCREEN_RECOVERY_OUTCOME_EVENT_NAME,
        createErrorScreenRecoveryOutcomeProperties(pending, outcome, now),
      );
      appInsights.flush();
    } catch {
      // Normal navigation must not fail because recovery telemetry is down.
    }
  }

  /**
   * Emits a recovery outcome synchronously, for the paths that cannot wait for a
   * landing: an `unmeasurable` record that could not be persisted, or a
   * `failure` where the action threw before it navigated. Idempotent per
   * incident via the resolved-recoveries guard.
   */
  recordRecoveryOutcome(
    pending: ErrorScreenPendingRecovery,
    outcome: UnicornRecoveryOutcome,
  ): void {
    if (this.resolvedRecoveries.has(pending.incidentCode)) {
      return;
    }
    this.emitOutcome(pending, outcome, this.getNow());
  }

  private isFailureDestination(destination: string): boolean {
    return (
      destination === '/global-error' ||
      destination === '/login' ||
      destination === '/verify-email' ||
      destination === '/page-not-found-404' ||
      destination.startsWith('/page-error-')
    );
  }

  private getStorage(): Storage | undefined {
    try {
      return this.document.defaultView?.sessionStorage ?? undefined;
    } catch {
      return undefined;
    }
  }

  private getNow(): number {
    try {
      return Date.now();
    } catch {
      return 0;
    }
  }

  /**
   * Reads the arrival handoff: the current navigation's state where available,
   * falling back to the browser history state (the state survives there once
   * navigation has completed, which is when a screen renders).
   */
  readHandoffState(): Record<string, unknown> | undefined {
    try {
      const navigationState = this.injector
        .get(Router, null)
        ?.getCurrentNavigation()
        ?.extras.state;
      if (navigationState) {
        return navigationState as Record<string, unknown>;
      }
    } catch {
      // Fall through to the browser history state.
    }

    try {
      return this.document.defaultView?.history.state as
        | Record<string, unknown>
        | undefined;
    } catch {
      return undefined;
    }
  }

  private getBuildIdentity(): string {
    try {
      return normalizeUnicornBuildIdentity(
        this.document.querySelector<HTMLMetaElement>(
          'meta[name="rettx-build-identity"]',
        )?.content,
      );
    } catch {
      return 'unresolved';
    }
  }

  private getConnectivity(): UnicornConnectivityState {
    try {
      const online = this.document.defaultView?.navigator?.onLine;
      return typeof online === 'boolean'
        ? online
          ? 'online'
          : 'offline'
        : 'unresolved';
    } catch {
      return 'unresolved';
    }
  }

  private getIncidentCode(): string {
    try {
      return createUnicornIncidentCode();
    } catch {
      return 'SAFE-2222';
    }
  }
}
